import { useMemo } from 'react';

import { polarToCartesian } from '../utils';

type RadialSliderHookProps = {
    radius: number;
    sliderWidth: number;
    openingRadian: number;
    thumbRadius: number;
    max: number;
    sliderPadding: number;
    sliderBorderWidth: number;
};

export const useRadialSlider = ({
    radius,
    sliderWidth,
    openingRadian,
    thumbRadius,
    max,
    sliderPadding,
    sliderBorderWidth,
}: RadialSliderHookProps) => {
    const radianValue = openingRadian;

    // Calculate layout values
    const totalStrokeWidth = sliderWidth + sliderPadding * 2 + sliderBorderWidth * 2;
    const centerOffset = radius + totalStrokeWidth / 2;

    // SVG size needs to accommodate the radius plus the maximum outward protrusion (either stroke or thumb)
    const svgSize = radius * 2 + Math.max(totalStrokeWidth, thumbRadius * 2);

    const startRadian = 2 * Math.PI - radianValue;

    // Get the starting point of the radial based on where the opening is
    const startPoint = polarToCartesian(startRadian, radius, centerOffset);

    // Get the end point of the radial based on where the opening is
    const endPoint = polarToCartesian(radianValue, radius, centerOffset);

    // Get all marker positions
    const markers = useMemo(() => {
        const points = [];
        for (let i = 0; i <= max; i++) {
            const radian = startRadian - ((startRadian - radianValue) * i) / max;
            const point = polarToCartesian(radian, radius, centerOffset);
            points.push({ point, value: i });
        }
        return points;
    }, [max, startRadian, radianValue, radius, centerOffset]);

    return {
        svgSize,
        startPoint,
        endPoint,
        startRadian,
        radianValue,
        markers,
        centerOffset,
        totalStrokeWidth,
    };
};
